import { useCallback, useState } from "react";
import { Loader } from "@mantine/core";
import { Sentiment, sentimentLabels } from "@edgetier/watchtower-types";
import { SentimentIcon } from "../../../sentiment-icon";
import { Menu } from "../../../menu";
import { Checkbox } from "../../../checkbox";
import { Group } from "../../../group";
import styles from "./chat.module.scss";
import { kebabCase } from "lodash";

/**
 * A menu item with checkbox toggle for enabling or disabling a specific sentiment on a message.
 * @param {boolean} props.value - Whether the sentiment is currently enabled.
 * @param {Sentiment} props.sentiment - The sentiment type to toggle.
 * @param {Function} props.onSentimentToggle - Callback invoked when the sentiment is toggled.
 * @returns {JSX.Element} The MessageOptionsSentiment component.
 */
const MessageOptionsSentiment = ({
    value,
    sentiment,
    onSentimentToggle,
}: {
    value: boolean;
    sentiment: Sentiment;
    onSentimentToggle: (sentiment: Sentiment) => Promise<void>;
}) => {
    const [isInternalLoading, setIsInternalLoading] = useState(false);

    const handleConfirm = useCallback(
        (sentiment: Sentiment) => {
            try {
                const result = onSentimentToggle(sentiment);

                // If result is a thenable (Promise-like), show loading state until it settles
                if (result && typeof result.then === "function") {
                    setIsInternalLoading(true);
                    return Promise.resolve(result)
                        .then((res) => {
                            setIsInternalLoading(false);
                            return res;
                        })
                        .catch((err) => {
                            setIsInternalLoading(false);
                            throw err;
                        });
                }

                // Synchronous result: return it directly, no loading state
                return result;
            } catch (err) {
                // If onConfirm throws synchronously, just rethrow
                throw err;
            }
        },
        [onSentimentToggle]
    );

    return (
        <Menu.Item
            key={`sentiment-toggler-${sentimentLabels[sentiment as Sentiment]}`}
            component="div"
            closeMenuOnClick={false}
            disabled={isInternalLoading}
            onClick={(event) => {
                event.preventDefault();
                handleConfirm(sentiment as Sentiment);
            }}
        >
            <Checkbox
                styles={{ labelWrapper: { flex: 1 } }}
                classNames={{ label: styles["checkbox-label"], input: styles["checkbox-input"] }}
                labelPosition="left"
                checked={value as boolean}
                label={
                    <Group>
                        {isInternalLoading ? (
                            <Loader size="xs" />
                        ) : (
                            <SentimentIcon sentiment={sentiment as Sentiment} />
                        )}
                        {sentimentLabels[sentiment as Sentiment]}
                    </Group>
                }
                data-testid={`${kebabCase(sentimentLabels[sentiment as Sentiment])}`}
                readOnly
            />
        </Menu.Item>
    );
};

export { MessageOptionsSentiment };
